import { rgbColor } from "./drawing";
import type { SnapshotEntity } from "./types";

export function drawFittedName(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  entity: SnapshotEntity
): void {
  const rawName = entity.name ?? "";
  const name = rawName.trim();

  if (!name) {
    return;
  }

  const nameColor = rgbColor(
    entity.cosmetics?.name_color ?? entity.cosmetics?.nameColor,
    "#ffffff"
  );

  const maxWidth = radius * 2.45;
  const maxLines = 3;
  let fontSize = Math.max(8, Math.min(28, radius * 0.42));
  let lines = wrapName(context, name, maxWidth, fontSize, maxLines);

  while (
    fontSize > 7 &&
    lines.some((line) => measureName(context, line, fontSize) > maxWidth)
  ) {
    fontSize -= 1;
    lines = wrapName(context, name, maxWidth, fontSize, maxLines);
  }

  const lineHeight = fontSize * 0.92;
  const totalHeight = lines.length * lineHeight;
  const startY = y - totalHeight * 0.5 + lineHeight * 0.5;

  context.save();
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `950 ${fontSize}px system-ui, sans-serif`;
  context.lineWidth = Math.max(3, fontSize * 0.22);
  context.strokeStyle = "rgba(0,0,0,0.38)";
  context.fillStyle = nameColor;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (line === undefined) {
      continue;
    }

    const lineY = startY + index * lineHeight;
    context.strokeText(line, x, lineY);
    context.fillText(line, x, lineY);
  }

  context.restore();
}

function wrapName(
  context: CanvasRenderingContext2D,
  name: string,
  maxWidth: number,
  fontSize: number,
  maxLines: number
): string[] {
  context.font = `950 ${fontSize}px system-ui, sans-serif`;

  const words = name.includes(" ")
    ? name.split(/\s+/).filter(Boolean)
    : splitIntoBalancedChunks(name);

  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;

    if (context.measureText(candidate).width <= maxWidth || current.length === 0) {
      current = candidate;
      continue;
    }

    lines.push(current);
    current = word;

    if (lines.length === maxLines - 1) {
      break;
    }
  }

  if (current) {
    lines.push(current);
  }

  return lines.slice(0, maxLines);
}

function splitIntoBalancedChunks(value: string): string[] {
  if (value.length <= 9) {
    return [value];
  }

  const chunks: string[] = [];
  const chunkCount = Math.min(3, Math.ceil(value.length / 9));
  const chunkSize = Math.ceil(value.length / chunkCount);

  for (let index = 0; index < value.length; index += chunkSize) {
    chunks.push(value.slice(index, index + chunkSize));
  }

  return chunks;
}

function measureName(
  context: CanvasRenderingContext2D,
  line: string,
  fontSize: number
): number {
  context.font = `950 ${fontSize}px system-ui, sans-serif`;
  return context.measureText(line).width;
}